import {
  promotionInputSchema,
  promotionPriceCents,
  type PromotionDto,
  type PromotionInput,
  type PromotionKind,
} from "@school-sales-ai/shared";
import { pesosToCents } from "@/lib/format-mxn";

/**
 * The editor's state and the pure functions over it.
 *
 * Colocated with the dialog and kept free of React so the tests assert the
 * validation and the price preview directly — the `availability-week.ts` /
 * `template-status.ts` pattern in this repo.
 *
 * Amounts live here as STRINGS, because that is what an input holds and because
 * `""` (nothing typed) and `"0"` (free) are different answers that a number
 * cannot tell apart.
 */
export type PromotionFormValues = {
  name: string;
  kind: PromotionKind;
  /** Pesos, as typed. Only meaningful when `kind === "price_override"`. */
  price: string;
  /** Percent, as typed — "12.5". Only meaningful when `kind === "percent_off"`. */
  percent: string;
  dateFrom: string;
  dateTo: string;
  weekdays: number[];
  /** "HH:MM", as an `<input type="time">` holds it. Empty = all day. */
  startTime: string;
  endTime: string;
  announce: boolean;
  productIds: string[];
};

export const WEEKDAY_LABELS = ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"];

export function emptyPromotionForm(): PromotionFormValues {
  return {
    name: "",
    kind: "percent_off",
    price: "",
    percent: "",
    dateFrom: "",
    dateTo: "",
    weekdays: [],
    startTime: "",
    endTime: "",
    // Announcing is the default; silence is the deliberate choice an admin makes.
    announce: true,
    productIds: [],
  };
}

export function promotionToForm(p: PromotionDto): PromotionFormValues {
  return {
    name: p.name,
    kind: p.kind,
    price: p.overridePriceCents === null ? "" : (p.overridePriceCents / 100).toFixed(2),
    percent: p.percentOffBp === null ? "" : String(p.percentOffBp / 100),
    dateFrom: p.dateFrom ?? "",
    dateTo: p.dateTo ?? "",
    weekdays: [...p.weekdays],
    startTime: minutesToTime(p.startMinute),
    endTime: minutesToTime(p.endMinute),
    announce: p.announce,
    productIds: p.products.map((x) => x.id),
  };
}

/** "18:30" → 1110. Empty or malformed → null (= all day). */
export function timeToMinutes(value: string): number | null {
  const m = /^(\d{1,2}):(\d{2})$/.exec(value.trim());
  if (!m) return null;
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (h > 23 || min > 59) return null;
  return h * 60 + min;
}

/** 1110 → "18:30". null → "". */
export function minutesToTime(minutes: number | null): string {
  if (minutes === null) return "";
  // 1440 is the exclusive end-of-day the schema allows; an input cannot show
  // "24:00", so it comes back as 23:59 and round-trips as one minute earlier.
  const clamped = Math.min(minutes, 23 * 60 + 59);
  return `${String(Math.floor(clamped / 60)).padStart(2, "0")}:${String(clamped % 60).padStart(2, "0")}`;
}

/** Percent as typed → basis points. "12.5" → 1250. Invalid → null. */
export function percentToBp(value: string): number | null {
  const trimmed = value.trim();
  // Whole-string match, for the same reason `pesosToCents` uses one:
  // `parseFloat("50abc")` is 50, and this number sets what a customer pays.
  if (!/^\d{1,3}(\.\d{1,2})?$/.test(trimmed)) return null;
  const bp = Math.round(Number(trimmed) * 100);
  return bp >= 1 && bp <= 10_000 ? bp : null;
}

/**
 * The form as the API body, or the list of problems.
 *
 * Validation is the SHARED schema, not a second set of rules here — Pattern B.
 * The browser gets inline errors from the same object Fastify rejects on, so a
 * form that passes here cannot be refused for a reason the admin never saw.
 */
export function toPromotionInput(
  values: PromotionFormValues,
): { ok: true; input: PromotionInput } | { ok: false; errors: Map<string, string> } {
  const candidate = {
    name: values.name,
    kind: values.kind,
    overridePriceCents: values.kind === "price_override" ? pesosToCents(values.price) : null,
    percentOffBp: values.kind === "percent_off" ? percentToBp(values.percent) : null,
    dateFrom: values.dateFrom.trim() === "" ? null : values.dateFrom,
    dateTo: values.dateTo.trim() === "" ? null : values.dateTo,
    weekdays: [...values.weekdays].sort((a, b) => a - b),
    startMinute: timeToMinutes(values.startTime),
    endMinute: timeToMinutes(values.endTime),
    announce: values.announce,
    productIds: values.productIds,
  };

  const parsed = promotionInputSchema.safeParse(candidate);
  if (parsed.success) return { ok: true, input: parsed.data };

  const errors = new Map<string, string>();
  for (const issue of parsed.error.issues) {
    const key = issue.path[0];
    if (typeof key === "string" && !errors.has(key)) errors.set(key, issue.message);
  }
  return { ok: false, errors };
}

/**
 * What one product will cost while this promotion is open.
 *
 * Uses the SAME arithmetic the backend charges with (`promotionPriceCents` in
 * `packages/shared`), so this preview is the price, not an estimate of it.
 * Returns null when the form does not yet describe a usable adjustment, or the
 * product has no list price — there is nothing to discount.
 */
export function previewPriceCents(
  basePriceCents: number | null,
  values: PromotionFormValues,
): number | null {
  if (basePriceCents === null) return null;
  const overridePriceCents =
    values.kind === "price_override" ? pesosToCents(values.price) : null;
  const percentOffBp = values.kind === "percent_off" ? percentToBp(values.percent) : null;
  if (values.kind === "price_override" && overridePriceCents === null) return null;
  if (values.kind === "percent_off" && percentOffBp === null) return null;
  return promotionPriceCents(basePriceCents, {
    kind: values.kind,
    overridePriceCents,
    percentOffBp,
  });
}

/**
 * Whether the agent would actually announce this, given what it does to a price.
 *
 * Mirrors `resolveEffectivePrice`'s downgrade so the editor cannot promise an
 * announcement the agent will refuse to make: a promotion that raises a price is
 * never presented to a customer as a promotion, whatever the toggle says.
 */
export function willAnnounce(
  values: PromotionFormValues,
  basePriceCents: number | null,
): boolean {
  if (!values.announce) return false;
  const preview = previewPriceCents(basePriceCents, values);
  if (preview === null || basePriceCents === null) return true;
  return preview < basePriceCents;
}

/** A one-line human summary of when this runs, for the list. */
export function describeWindow(p: PromotionDto): string {
  const parts: string[] = [];
  if (p.dateFrom && p.dateTo) parts.push(`${p.dateFrom} a ${p.dateTo}`);
  else if (p.dateFrom) parts.push(`desde ${p.dateFrom}`);
  else if (p.dateTo) parts.push(`hasta ${p.dateTo}`);

  if (p.weekdays.length > 0 && p.weekdays.length < 7) {
    parts.push(p.weekdays.map((d) => WEEKDAY_LABELS[d]).join(", "));
  }
  if (p.startMinute !== null && p.endMinute !== null) {
    parts.push(`${minutesToTime(p.startMinute)}–${minutesToTime(p.endMinute)}`);
  }
  // Nothing constrained at all is a promotion that is simply always on, and
  // saying so is more useful than an empty cell.
  return parts.length > 0 ? parts.join(" · ") : "Siempre";
}
